import React, { useEffect, useRef, useState } from 'react';
import EvidenceLayer from '../../components/EvidenceLayer';
import SemaforoBadge from '../../components/SemaforoBadge';
import StatusBadge from '../../components/StatusBadge';
import { displayStatus, evaluationPercentage } from '../../lib/evaluationIntegrity';
import apiClient, { evaluationsAPI } from '../../api/client';
import { MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { buildInfoWindowContent } from '../../lib/infoWindowContent';

const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;

const MARKER_TYPES = {
  hospital: { label: 'Hospital', color: '#DC2626', icon: '🏥' },
  evacuacion: { label: 'Ruta de Evacuación', color: '#1A56DB', icon: '🚶' },
  riesgo: { label: 'Zona de Riesgo', color: '#D97706', icon: '⚠' },
  critico: { label: 'Punto Crítico', color: '#7C3AED', icon: '📍' },
  location: { label: 'Locación', color: '#0D9488', icon: '📍' },
};

export default function ClienteMapPage() {
  const mapRef = useRef(null);
  const googleMapRef = useRef(null);
  const [evidenceMap, setEvidenceMap] = useState(null);
  const [evaluations, setEvaluations] = useState([]);
  const [selectedEval, setSelectedEval] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  // #A13 — antes EXAMPLE_MARKERS pintaba 5 puntos inventados (hospital,
  // rutas de evacuación, zona de riesgo) SIEMPRE en el centro de CDMX,
  // sin importar dónde estuviera el recinto real del cliente. Ahora se
  // cargan los map_points reales de la evaluación seleccionada.
  const [mapPoints, setMapPoints] = useState([]);
  const [loadingPoints, setLoadingPoints] = useState(false);

  useEffect(() => {
    evaluationsAPI.list()
      .then((r) => {
        const pubs = (r.data.items || []).filter((e) => e.status === 'publicada');
        setEvaluations(pubs);
        if (pubs.length > 0) setSelectedEval(pubs[0]);
      })
      .catch(() => toast.error('Error al cargar evaluaciones'));
  }, []);

  useEffect(() => {
    if (!selectedEval?.id) { setMapPoints([]); return; }
    setLoadingPoints(true);
    apiClient.get('/map-points', { params: { evaluation_id: selectedEval.id } })
      .then((r) => setMapPoints(r.data.items || []))
      .catch(() => setMapPoints([]))
      .finally(() => setLoadingPoints(false));
  }, [selectedEval?.id]);

  useEffect(() => {
    if (!window.google && !document.getElementById('gmaps-script')) {
      const script = document.createElement('script');
      script.id = 'gmaps-script';
      script.src = `https://maps.googleapis.com/maps/api/js?key=${MAPS_KEY}&libraries=places`;
      script.async = true;
      script.onload = () => setMapLoaded(true);
      document.head.appendChild(script);
    } else if (window.google) {
      setMapLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (!mapLoaded || !mapRef.current) return;
    // #A13 — el centro ahora usa las coordenadas reales de la locación
    // (precargadas en location_info al crear la evaluación), no el
    // centro fijo de CDMX. Si la locación no tiene GPS capturado, se usa
    // el primer map_point real como referencia antes de caer al default.
    const locLat = selectedEval?.location_info?.lat;
    const locLng = selectedEval?.location_info?.lng;
    const firstPoint = mapPoints[0];
    const center = (locLat && locLng)
      ? { lat: locLat, lng: locLng }
      : firstPoint
        ? { lat: firstPoint.lat, lng: firstPoint.lng }
        : { lat: 19.4326, lng: -99.1332 };

    const map = new window.google.maps.Map(mapRef.current, {
      center,
      zoom: 15,
      styles: [
        { elementType: 'geometry', stylers: [{ color: '#f5f5f5' }] },
        { elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
        { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
        { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#c9d2d3' }] },
      ],
    });
    googleMapRef.current = map;
    setEvidenceMap(map);

    // Add markers — datos reales de la evaluación (map_points), ya no
    // EXAMPLE_MARKERS hardcodeados.
    mapPoints.forEach((m) => {
      if (m.lat == null || m.lng == null) return;
      const type = MARKER_TYPES[m.category] ? m.category : 'critico';
      const marker = new window.google.maps.Marker({
        position: { lat: m.lat, lng: m.lng },
        map,
        title: m.title || MARKER_TYPES[type].label,
        label: { text: (m.title || MARKER_TYPES[type].label).charAt(0).toUpperCase(), color: 'white', fontWeight: 'bold', fontSize: '12px' },
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 16,
          fillColor: MARKER_TYPES[type].color,
          fillOpacity: 1,
          strokeColor: 'white',
          strokeWeight: 2,
        },
      });
      const infoWindow = new window.google.maps.InfoWindow({
        content: buildInfoWindowContent({
          title: m.title || MARKER_TYPES[type].label,
          label: MARKER_TYPES[type].label,
          notes: m.notes,
        }),
      });
      marker.addListener('click', () => infoWindow.open(map, marker));
    });
  }, [mapLoaded, selectedEval, mapPoints]);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="cliente-map-page">
      <div>
        <h1 className="text-2xl font-bold text-soem-navy">Mapa Interactivo</h1>
        <p className="text-slate-500 text-sm mt-1">Visualiza los puntos críticos del recinto evaluado.</p>
      </div>

      {/* Eval selector */}
      {evaluations.length > 1 && (
        <div className="flex items-center gap-3">
          <label htmlFor="f-evaluación" className="soem-label">Evaluación:</label>
          <select
id="f-evaluación"             className="px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary"
            value={selectedEval?.id || ''}
            onChange={(e) => setSelectedEval(evaluations.find((ev) => ev.id === e.target.value))}
            data-testid="map-eval-select"
          >
            {evaluations.map((e) => (
              <option key={e.id} value={e.id}>
                {e.general_fields?.instalacion || 'Evaluación'} v{e.version}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Map */}
        <div className="lg:col-span-3">
          <div
            ref={mapRef}
            className="w-full rounded-sm border border-slate-200 shadow-sm"
            style={{ height: '500px' }}
            data-testid="google-map"
          >
            {!mapLoaded && (
              <div className="w-full h-full flex items-center justify-center bg-slate-100">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-4 border-soem-primary border-t-transparent mx-auto mb-3" />
                  <p className="text-slate-500 text-sm">Cargando mapa...</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Legend */}
        <div className="space-y-4">
          <div className="soem-card p-4">
            <p className="soem-label mb-3">Leyenda</p>
            <div className="space-y-2">
              {Object.entries(MARKER_TYPES).map(([key, val]) => (
                <div key={key} className="flex items-center gap-2.5">
                  <div className="w-6 h-6 rounded-full flex-shrink-0" style={{ backgroundColor: val.color }} />
                  <span className="text-sm text-soem-navy">{val.label}</span>
                </div>
              ))}
            </div>
          </div>

          {selectedEval && (
            <div className="soem-card p-4">
              <p className="soem-label mb-3">Locación Activa</p>
              <p className="font-bold text-soem-navy text-sm">{selectedEval.general_fields?.instalacion || '—'}</p>
              {selectedEval.general_fields?.ubicacion && (
                <p className="text-slate-500 text-xs mt-1 flex items-start gap-1">
                  <MapPin size={12} className="flex-shrink-0 mt-0.5" />
                  {selectedEval.general_fields.ubicacion}
                </p>
              )}
              <div className="mt-3">
                <p className="soem-label mb-1">Riesgo</p>
                <div className="flex items-center gap-2">
                  <SemaforoBadge semaforo={selectedEval.semaforo}
                    percentage={evaluationPercentage(selectedEval)} size="sm" />
                  <StatusBadge status={displayStatus(selectedEval)} />
                </div>
              </div>
            </div>
          )}

          <div className="soem-card p-4">
            <p className="soem-label mb-3">Marcadores ({mapPoints.length})</p>
            <div className="space-y-1.5">
              {loadingPoints ? (
                <p className="text-xs text-slate-400">Cargando...</p>
              ) : mapPoints.length === 0 ? (
                <p className="text-xs text-slate-400">Esta evaluación no tiene puntos de seguridad marcados.</p>
              ) : mapPoints.map((m) => (
                <div key={m.id} className="flex items-center gap-2 text-xs">
                  <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: MARKER_TYPES[MARKER_TYPES[m.category] ? m.category : 'critico'].color }} />
                  <span className="text-slate-600">{m.title || MARKER_TYPES[MARKER_TYPES[m.category] ? m.category : 'critico'].label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <EvidenceLayer evaluationId={selectedEval?.id} map={evidenceMap} />
    </div>
  );
}
